import { apiRequest } from '../../../shared/api';
import {
  RegulatoryAuthority,
  RegulatoryFramework,
  RegulatoryDocument,
  RegulatoryDocumentVersion,
  RegulatoryRequirement,
  OrganizationFramework,
  OrganizationRequirementApplicability,
  RequirementControlMapping,
  RegulatoryChange,
  RegulatoryImpactAssessment,
  RegulatoryDashboardStats,
  ApplicabilityStatus,
  DecisionSource,
} from './types';

export const regulatoryService = {
  getDashboardStats: (organizationId: string = 'ORG-XYZ') =>
    apiRequest<RegulatoryDashboardStats>(`/compliance/regulatory/analytics/dashboard?organizationId=${organizationId}`),

  getAuthorities: () =>
    apiRequest<RegulatoryAuthority[]>('/compliance/regulatory/authorities'),

  getFrameworks: (filter: { authorityId?: string; frameworkType?: string; category?: string } = {}) => {
    const query = new URLSearchParams();
    if (filter.authorityId) query.append('authorityId', filter.authorityId);
    if (filter.frameworkType) query.append('frameworkType', filter.frameworkType);
    if (filter.category) query.append('category', filter.category);
    return apiRequest<RegulatoryFramework[]>(`/compliance/regulatory/frameworks?${query.toString()}`);
  },

  getFrameworkById: (id: string) =>
    apiRequest<RegulatoryFramework>(`/compliance/regulatory/frameworks/${id}`),

  getDocuments: (frameworkId?: string) => {
    const query = new URLSearchParams();
    if (frameworkId) query.append('frameworkId', frameworkId);
    return apiRequest<RegulatoryDocument[]>(`/compliance/regulatory/documents?${query.toString()}`);
  },

  getDocumentById: (id: string) =>
    apiRequest<RegulatoryDocument>(`/compliance/regulatory/documents/${id}`),

  getDocumentVersions: (docId: string) =>
    apiRequest<RegulatoryDocumentVersion[]>(`/compliance/regulatory/documents/${docId}/versions`),

  getRequirements: (filter: {
    frameworkId?: string;
    documentId?: string;
    versionId?: string;
    status?: string;
    requirementType?: string;
    query?: string;
  } = {}) => {
    const query = new URLSearchParams();
    if (filter.frameworkId) query.append('frameworkId', filter.frameworkId);
    if (filter.documentId) query.append('documentId', filter.documentId);
    if (filter.versionId) query.append('versionId', filter.versionId);
    if (filter.status) query.append('status', filter.status);
    if (filter.requirementType) query.append('requirementType', filter.requirementType);
    if (filter.query) query.append('query', filter.query);
    return apiRequest<RegulatoryRequirement[]>(`/compliance/regulatory/requirements?${query.toString()}`);
  },

  getRequirementById: (id: string, organizationId: string = 'ORG-XYZ') =>
    apiRequest<RegulatoryRequirement>(`/compliance/regulatory/requirements/${id}?organizationId=${organizationId}`),

  createRequirement: (data: {
    documentVersionId: string;
    nodeId?: string;
    requirementCode: string;
    title: string;
    requirementText: string;
    requirementType?: string;
    normativeLevel?: string;
    isMandatory?: boolean;
  }) =>
    apiRequest<RegulatoryRequirement>('/compliance/regulatory/requirements', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateRequirement: (id: string, data: {
    title?: string;
    requirementText?: string;
    requirementType?: string;
    normativeLevel?: string;
    status?: string;
    changeSummary?: string;
  }) =>
    apiRequest<RegulatoryRequirement>(`/compliance/regulatory/requirements/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getOrganizationFrameworks: (organizationId: string = 'ORG-XYZ') =>
    apiRequest<OrganizationFramework[]>(`/compliance/regulatory/organization-frameworks?organizationId=${organizationId}`),

  setOrganizationFrameworkApplicability: (data: {
    organizationId: string;
    frameworkId: string;
    isApplicable: boolean;
    justification?: string;
  }) =>
    apiRequest<OrganizationFramework>('/compliance/regulatory/organization-frameworks', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getOrganizationRequirementApplicability: (organizationId: string = 'ORG-XYZ', requirementId?: string) => {
    const query = new URLSearchParams();
    query.append('organizationId', organizationId);
    if (requirementId) query.append('requirementId', requirementId);
    return apiRequest<OrganizationRequirementApplicability[]>(`/compliance/regulatory/organization-requirements?${query.toString()}`);
  },

  setOrganizationRequirementApplicability: (data: {
    organizationId: string;
    requirementId: string;
    applicabilityStatus: ApplicabilityStatus;
    decisionSource?: DecisionSource;
    whyApplicable?: string;
    whyNotApplicable?: string;
    conditions?: string;
    reviewerId?: string;
    reviewerName?: string;
  }) =>
    apiRequest<OrganizationRequirementApplicability>('/compliance/regulatory/organization-requirements', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getControlMappings: (organizationId: string = 'ORG-XYZ', requirementId?: string) => {
    const query = new URLSearchParams();
    query.append('organizationId', organizationId);
    if (requirementId) query.append('requirementId', requirementId);
    return apiRequest<RequirementControlMapping[]>(`/compliance/regulatory/control-mappings?${query.toString()}`);
  },

  createControlMapping: (data: {
    organizationId: string;
    requirementId: string;
    controlReference: string;
    controlTitle: string;
    controlType?: string;
    complianceLevel?: 'COMPLIANT' | 'PARTIALLY_COMPLIANT' | 'NON_COMPLIANT' | 'PLANNED';
    notes?: string;
    mappedBy?: string;
  }) =>
    apiRequest<RequirementControlMapping>('/compliance/regulatory/control-mappings', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getChanges: (documentId?: string) => {
    const query = new URLSearchParams();
    if (documentId) query.append('documentId', documentId);
    return apiRequest<RegulatoryChange[]>(`/compliance/regulatory/changes?${query.toString()}`);
  },

  getChangeById: (id: string) =>
    apiRequest<RegulatoryChange>(`/compliance/regulatory/changes/${id}`),

  createChange: (data: {
    documentId: string;
    fromVersionId?: string;
    toVersionId: string;
    title: string;
    changeType?: string;
    noticeDate: string;
    effectiveDate: string;
    summary?: string;
  }) =>
    apiRequest<RegulatoryChange>('/compliance/regulatory/changes', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getImpactAssessments: (organizationId: string = 'ORG-XYZ', changeId?: string) => {
    const query = new URLSearchParams();
    query.append('organizationId', organizationId);
    if (changeId) query.append('changeId', changeId);
    return apiRequest<RegulatoryImpactAssessment[]>(`/compliance/regulatory/impact-assessments?${query.toString()}`);
  },

  createImpactAssessment: (data: {
    changeId: string;
    organizationId: string;
    assessorId: string;
    assessorName: string;
    affectedDepartments?: string;
    affectedManuals?: string;
    requiresMoc?: boolean;
    requiresTraining?: boolean;
    requiresAudit?: boolean;
    requiresRiskAssessment?: boolean;
    summary: string;
  }) =>
    apiRequest<RegulatoryImpactAssessment>('/compliance/regulatory/impact-assessments', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  approveImpactAssessment: (id: string, approvedBy: string = 'ACCOUNTABLE_MGR') =>
    apiRequest<RegulatoryImpactAssessment>(`/compliance/regulatory/impact-assessments/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approvedBy }),
    }),
};
